// Wave 20.50 — Aero heads-up: turning the server's structured cues into
// words. IDENTICAL FILE on web and mobile (web/src/services/aeroCues.ts,
// mobile/src/services/aeroCues.ts) — copy edits to both or the two apps
// start saying different things about the same wind.
//
// The server decides WHAT is happening (see platform aero-brief.ts); this
// decides how to say it, because phrasing is display, and display honours
// the rider's unit setting. Two renderings per cue:
//   text   — for the screen: "0.25 mi · cross-head from the left, 14 mph"
//   speech — for the ear: "Turning right in a quarter mile, into a
//            cross-head wind from the left at 14 miles per hour."
// Spoken text never contains abbreviations, digits-with-units, or
// punctuation a TTS engine reads aloud badly.

export type WindRelative =
  | 'headwind'
  | 'cross_head_left'
  | 'cross_head_right'
  | 'cross_left'
  | 'cross_right'
  | 'cross_tail_left'
  | 'cross_tail_right'
  | 'tailwind';

export type CueType = 'turn' | 'tuck_start' | 'tuck_end' | 'wind_shift';

export interface CueWind {
  speedMps: number;
  fromDeg: number;
  headwindMps: number;
  crosswindMps: number;
  relative: WindRelative;
  side: 'left' | 'right' | null;
}

export interface Cue {
  id: string;
  type: CueType;
  atM: number;
  turn?: 'left' | 'right';
  wind: CueWind;
  zoneLengthM?: number;
  zoneSavingsSec?: number;
}

export type Units = 'metric' | 'imperial';

const MI_PER_M = 1 / 1609.344;
const FT_PER_M = 3.280839895;

/** Short screen form: "0.25 mi", "400 m". */
export function cueDistanceText(m: number, units: Units): string {
  if (units === 'imperial') {
    const mi = m * MI_PER_M;
    if (mi < 0.095) return `${Math.round(m * FT_PER_M / 10) * 10} ft`;
    return `${mi < 1 ? mi.toFixed(2) : mi.toFixed(1)} mi`;
  }
  if (m < 950) return `${Math.round(m / 10) * 10} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

/** Spoken form. Riders think in fractions of a mile, not "zero point two
 *  five miles", so snap to the fractions a road sign would use. */
export function cueDistanceSpeech(m: number, units: Units): string {
  if (units === 'imperial') {
    const mi = m * MI_PER_M;
    if (mi < 0.07) return `${Math.round(m * FT_PER_M / 50) * 50} feet`;
    if (mi < 0.14) return 'a tenth of a mile';
    if (mi < 0.31) return 'a quarter mile';
    if (mi < 0.42) return 'a third of a mile';
    if (mi < 0.62) return 'half a mile';
    if (mi < 0.87) return 'three quarters of a mile';
    if (mi < 1.3) return 'a mile';
    return `${mi.toFixed(1)} miles`;
  }
  if (m < 950) return `${Math.round(m / 50) * 50} meters`;
  const km = m / 1000;
  if (km < 1.3) return 'a kilometer';
  return `${km.toFixed(1)} kilometers`;
}

export function windSpeedText(mps: number, units: Units): string {
  return units === 'imperial'
    ? `${Math.round(mps * 2.23694)} mph`
    : `${Math.round(mps * 3.6)} km/h`;
}

export function windSpeedSpeech(mps: number, units: Units): string {
  return units === 'imperial'
    ? `${Math.round(mps * 2.23694)} miles per hour`
    : `${Math.round(mps * 3.6)} kilometers per hour`;
}

const RELATIVE_LONG: Record<WindRelative, string> = {
  headwind: 'a headwind',
  cross_head_left: 'a cross-head wind from the left',
  cross_head_right: 'a cross-head wind from the right',
  cross_left: 'a crosswind from the left',
  cross_right: 'a crosswind from the right',
  cross_tail_left: 'a cross-tail wind from the left',
  cross_tail_right: 'a cross-tail wind from the right',
  tailwind: 'a tailwind',
};

const RELATIVE_SHORT: Record<WindRelative, string> = {
  headwind: 'Headwind',
  cross_head_left: 'Cross-head from the left',
  cross_head_right: 'Cross-head from the right',
  cross_left: 'Crosswind from the left',
  cross_right: 'Crosswind from the right',
  cross_tail_left: 'Cross-tail from the left',
  cross_tail_right: 'Cross-tail from the right',
  tailwind: 'Tailwind',
};

export function windRelativeText(r: WindRelative): string {
  return RELATIVE_SHORT[r];
}

export function windRelativeSpeech(r: WindRelative): string {
  return RELATIVE_LONG[r];
}

/** Under a genuine breeze the quadrant is noise — don't announce a
 *  direction we can't stand behind. */
const WIND_MENTION_FLOOR_MPS = 1.5;

/** One line for the screen. `distanceToM` is how far AHEAD the cue is;
 *  pass null for a static list (the planner's brief), where the cue's
 *  own position along the route is what matters. */
export function cueText(cue: Cue, units: Units, distanceToM: number | null): string {
  const where = distanceToM == null ? cueDistanceText(cue.atM, units) : `in ${cueDistanceText(distanceToM, units)}`;
  const windy = cue.wind.speedMps >= WIND_MENTION_FLOOR_MPS;
  const wind = windy
    ? `${windRelativeText(cue.wind.relative)}, ${windSpeedText(cue.wind.speedMps, units)}`
    : 'light wind';
  switch (cue.type) {
    case 'turn':
      return `${where} · turn ${cue.turn} · ${wind}`;
    case 'tuck_start': {
      const len = cue.zoneLengthM != null ? cueDistanceText(cue.zoneLengthM, units) : '';
      const save = cue.zoneSavingsSec != null ? ` · saves ~${Math.round(cue.zoneSavingsSec)}s` : '';
      return `${where} · tuck for ${len} · ${wind}${save}`;
    }
    case 'tuck_end':
      return `${where} · end of the aero stretch`;
    case 'wind_shift':
      return `${where} · wind shifts to ${wind}`;
  }
}

/** One sentence for the ear. Returns null when the cue isn't worth
 *  interrupting the rider for. */
export function cueSpeech(cue: Cue, units: Units, distanceToM: number): string | null {
  // Close enough that a distance would be noise — and by the time the
  // sentence finishes they're on top of it.
  const near = distanceToM < 60;
  const where = near ? 'now' : `in ${cueDistanceSpeech(distanceToM, units)}`;
  const windy = cue.wind.speedMps >= WIND_MENTION_FLOOR_MPS;
  const wind = `${windRelativeSpeech(cue.wind.relative)} at ${windSpeedSpeech(cue.wind.speedMps, units)}`;
  switch (cue.type) {
    case 'turn':
      // The whole point of the feature: what you are turning INTO.
      if (!windy) return near ? `Turn ${cue.turn} now.` : `Turning ${cue.turn} ${where}.`;
      return near
        ? `Turn ${cue.turn} now, into ${wind}.`
        : `Turning ${cue.turn} ${where}, into ${wind}.`;
    case 'tuck_start': {
      if (cue.zoneLengthM == null) return null;
      const len = cueDistanceSpeech(cue.zoneLengthM, units);
      const save =
        cue.zoneSavingsSec != null && cue.zoneSavingsSec >= 5
          ? ` Worth about ${Math.round(cue.zoneSavingsSec)} seconds.`
          : '';
      const lead = near ? 'Aero stretch now.' : `Aero stretch ${where}.`;
      return `${lead} ${len} of ${windy ? wind : 'headwind'}. Hold your tuck.${save}`;
    }
    case 'tuck_end':
      return 'End of the aero stretch. Sit up.';
    case 'wind_shift':
      if (!windy) return null;
      return `Wind shifting to ${wind}.`;
  }
}
